import { Add } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import type { FC } from 'react';

import { hasValue } from '@core/utils/equality';
import type {
  NotificationFilterValues,
  NotificationSortOption,
} from '@core/utils/notificationQueryParams';
import type { NotificationScopeObjectType, NotificationTab } from '@core/utils/notifications';
import { useNotificationPanel } from '@web/providers/NotificationPanelProvider/NotificationPanelProvider';

import InputSearch from '../InputSearch/InputSearch';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import { SlabDrawerHeader } from '../SlabDrawer/SlabDrawerHeader';
import { NotificationCenterFilters } from './NotificationCenterFilters';
import { NotificationCenterSorting } from './NotificationCenterSorting';
import { NotificationCenterTabs } from './NotificationCenterTabs';

const CURRENT_OBJECT_LABEL: Record<NotificationScopeObjectType, string> = {
  quote: 'Current Quote',
  project: 'Current Project',
  activity: 'Current Activity',
};

type Props = {
  search: string;
  onSearchChange: (value: string) => void;
  tab: NotificationTab;
  onTabChange: (tab: NotificationTab) => void;
  appliedSearch: string;
  filterValues: NotificationFilterValues;
  onFilterValuesChange: (value: NotificationFilterValues) => void;
  sortOption: NotificationSortOption;
  onSortOptionChange: (value: NotificationSortOption) => void;
  onAddReminder: () => void;
};

export const NotificationCenterHeader: FC<Props> = ({
  search,
  onSearchChange,
  tab,
  onTabChange,
  appliedSearch,
  filterValues,
  onFilterValuesChange,
  sortOption,
  onSortOptionChange,
  onAddReminder,
}) => {
  const { close, scope, setScope, scopeObject } = useNotificationPanel();

  const scopeItems = [
    { id: 'all', label: 'All Notifications' },
    ...(!hasValue(scopeObject)
      ? []
      : [{ id: 'currentObject', label: CURRENT_OBJECT_LABEL[scopeObject.type] }]),
  ];

  const onScopeChange = (value: string): void =>
    setScope(value === 'currentObject' ? 'currentObject' : 'all');

  return (
    <Box
      bgcolor='SlabIndigo.50'
      paddingX={2}
      paddingTop={2}
      borderBottom='1px solid'
      borderColor='SlabGray.Stroke'
      display='flex'
      flexDirection='column'
      gap={1}
    >
      <SlabDrawerHeader
        title={<Typography variant='h2'>Notifications</Typography>}
        onClose={close}
        sx={{ marginBottom: 1.5 }}
      />

      <Box display='flex' alignItems='center' gap={1.5}>
        <SegmentedControl
          value={scope}
          items={scopeItems}
          onChange={onScopeChange}
          fitContent
          sx={{ backgroundColor: 'SlabGray.0', borderRadius: '0.75rem' }}
        />
        <Divider orientation='vertical' flexItem sx={{ marginLeft: 'auto', mt: 0, mb: 0 }} />

        <Button variant='slab-action-bordered' startIcon={<Add />} onClick={onAddReminder}>
          Add Reminder
        </Button>
      </Box>
      <Box display='flex' alignItems='center' gap={1.5} sx={{ marginTop: 1.5 }}>
        <InputSearch
          value={search}
          onChange={onSearchChange}
          sx={{ borderColor: 'SlabGray.Stroke', flex: 1 }}
        />

        <NotificationCenterFilters tab={tab} value={filterValues} onChange={onFilterValuesChange} />
        <NotificationCenterSorting tab={tab} value={sortOption} onChange={onSortOptionChange} />
      </Box>

      <NotificationCenterTabs
        value={tab}
        onChange={onTabChange}
        search={appliedSearch}
        filterValues={filterValues}
      />
    </Box>
  );
};
